import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertTriangle, CheckCircle2, XCircle, Loader2, RefreshCw,
  ShieldCheck, FileText, Ban, Wand2, Download,
} from 'lucide-react';
import PressPdfStatusChip from './PressPdfStatusChip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const statusStyle = (s) => {
  switch (s) {
    case 'committed': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    case 'rejected':  return 'bg-gray-200 text-gray-700 border-gray-300';
    case 'error':     return 'bg-rose-100 text-rose-800 border-rose-300';
    default:          return 'bg-amber-100 text-amber-800 border-amber-300'; // pending
  }
};

const BPTextFixBatchPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(null); // null = all pending
  const [retryingKey, setRetryingKey] = useState(null);
  // v6.35.34: per-scene dismiss-error tracker + hide-committed toggle
  const [dismissingKey, setDismissingKey] = useState(null);
  const [hideCommitted, setHideCommitted] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bp-auditor/text-fix/review`, authCfg());
      setData(data);
    } catch (e) {
      toast.error(`Load review failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rejectScene = async (s) => {
    if (!window.confirm(`Reject the candidate for "${s.context}" (Grade ${s.grade})? The current image stays in place. You can regenerate a new candidate individually from the Fix Image dialog below.`)) return;
    try {
      await axios.post(`${API}/bp-auditor/text-fix/reject`, {
        grade: s.grade, context: s.context,
      }, authCfg());
      toast.success(`Rejected: ${s.context}`);
      await load();
    } catch (e) {
      toast.error(`Reject failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const unrejectScene = async (s) => {
    try {
      await axios.post(`${API}/bp-auditor/text-fix/unreject`, {
        grade: s.grade, context: s.context,
      }, authCfg());
      toast.success(`Restored to pending: ${s.context}`);
      await load();
    } catch (e) {
      toast.error(`Restore failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const retryScene = async (s) => {
    const key = `${s.grade}|${s.context}`;
    setRetryingKey(key);
    try {
      const { data } = await axios.post(`${API}/bp-auditor/text-fix/retry`, {
        grade: s.grade, context: s.context,
      }, { ...authCfg(), timeout: 90000 });
      toast.success(`Retry succeeded — new candidate ready (${Math.round((data.bytes || 0) / 1024)} KB)`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Retry failed: ${detail}`, { duration: 8000 });
    } finally {
      setRetryingKey(null);
    }
  };

  // v6.35.34: dismiss a stale/historical error string (e.g. LiteLLM
  // budget-cap message from before the direct-LLM cutover) so the card
  // leaves the error bucket without re-running a full batch regen.
  const dismissError = async (s) => {
    const key = `${s.grade}|${s.context}`;
    if (!window.confirm(
      `Dismiss the historical generation error for "${s.context}" (Grade ${s.grade})?\n\n` +
      `This nulls the stored error string and the empty candidate URL so the card ` +
      `leaves the Errors bucket. The live student-facing image is NOT touched. ` +
      `Audit-logged. Reversible only by re-running a regen.`
    )) return;
    setDismissingKey(key);
    try {
      await axios.post(`${API}/bp-auditor/text-fix/dismiss-error`, {
        grade: s.grade, context: s.context,
      }, authCfg());
      toast.success(`Dismissed historical error: ${s.context}`);
      await load();
    } catch (e) {
      toast.error(`Dismiss failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDismissingKey(null);
    }
  };

  // v6.36.43: bulk-dismiss every error-bucket card in one call.
  // Use case: cleanup of the 10 stuck Batch Text-in-Image Fix cards
  // left over from the pre-direct-LLM budget-cap errors.
  const dismissAllErrors = async () => {
    const errorCount = counts.error || 0;
    if (!errorCount) {
      toast.info('No error cards to dismiss.');
      return;
    }
    if (!window.confirm(
      `Dismiss all ${errorCount} historical generation error(s)?\n\n` +
      `Each card's stored error string and empty candidate URL will be nulled ` +
      `so they leave the Errors bucket. NO live student-facing image is touched. ` +
      `NO questions / assessments / answer keys / TEs / BLMs are touched. ` +
      `Audit-logged as a single consolidated entry. Reversible only by re-running a regen.`
    )) return;
    setDismissingKey('__bulk__');
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/text-fix/dismiss-all-errors`, {}, authCfg(),
      );
      toast.success(
        `Dismissed ${data.dismissed} error card(s) · skipped ${data.skipped}`,
      );
      await load();
    } catch (e) {
      toast.error(`Bulk-dismiss failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDismissingKey(null);
    }
  };

  const downloadCertificate = async () => {
    const t = toast.loading('Generating remediation certificate…', { duration: Infinity });
    try {
      const res = await axios.get(`${API}/bp-auditor/text-fix/certificate`, {
        ...authCfg(), responseType: 'blob', timeout: 60000,
      });
      toast.dismiss(t);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC_WIDA_Remediation_Certificate_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded.');
    } catch (e) {
      toast.dismiss(t);
      // blob responses need a different parse path for error details
      let msg = e.message;
      try {
        const text = await e.response?.data?.text?.();
        if (text) { try { msg = JSON.parse(text).detail || text; } catch { msg = text; } }
      } catch {}
      toast.error(`Certificate failed: ${msg}`);
    }
  };

  const openApproveAll = (scope) => {
    setSelectedOnly(scope);  // null for all pending, or array of {grade, context}
    setPw('');
    setPwOpen(true);
  };

  const confirmApprove = async () => {
    if (!pw) { toast.error('Password required'); return; }
    setApproving(true);
    // Close the dialog immediately so the user sees the committing state on the panel, not blocked behind the modal
    setPwOpen(false);
    const commitToast = toast.loading(
      selectedOnly
        ? `Committing ${selectedOnly.length} scene(s)…`
        : `Committing ${counts.pending || 0} scene(s)… This can take 20-40 seconds.`,
      { duration: Infinity }
    );
    try {
      const body = { password: pw };
      if (selectedOnly) body.only = selectedOnly;
      const { data } = await axios.post(
        `${API}/bp-auditor/text-fix/approve-all`, body, { ...authCfg(), timeout: 180000 }
      );
      toast.dismiss(commitToast);
      toast.success(
        `Committed ${data.counts.committed} · Skipped ${data.counts.skipped} · Errors ${data.counts.errors}`,
        { duration: 6000 }
      );
      if (data.press_regen_triggered) {
        toast.info(
          '112 press-PDFs regenerating in the background (~2-3 min). Poll via Content Auditor → Regen Press PDFs status panel.',
          { duration: 10000 }
        );
      }
      setPw('');
      await load();
    } catch (e) {
      toast.dismiss(commitToast);
      toast.error(`Approve failed: ${e?.response?.data?.detail || e.message}`);
      // Reopen dialog so the user can retry without re-typing from scratch
      setPwOpen(true);
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-xl p-6 text-sm text-gray-500">
        <Loader2 className="inline-block animate-spin mr-2" size={14} /> Checking for batch text-violation fixes…
      </div>
    );
  }

  if (!data?.exists) {
    return null; // no batch review payload — silently hide
  }

  const counts = data.counts || {};
  const scenes = data.scenes || [];

  return (
    <div className="bg-white border border-rose-200 rounded-xl p-6 space-y-4" data-testid="bp-text-fix-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={20} className="text-rose-600" />
            <h3 className="text-lg font-bold">Batch Text-in-Image Fix (WIDA Compliance)</h3>
            <Badge className="bg-rose-100 text-rose-800 border-rose-300 border" variant="outline">
              {counts.total || 0} scenes flagged
            </Badge>
          </div>
          <p className="text-xs text-gray-600 max-w-3xl">
            MOSAIC content engine Vision flagged these Big Picture scenes for containing readable text — a strict WIDA violation.
            MOSAIC image engine has generated text-free replacement candidates that also re-anchor every missing
            correct answer. Review side-by-side below, reject any you don't want, then bulk-commit the rest.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PressPdfStatusChip />
          <Button variant="outline" size="sm" onClick={load} data-testid="bp-text-fix-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          {(counts.committed || 0) > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCertificate}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="bp-text-fix-download-cert"
            >
              <Download size={14} className="mr-1" />
              Download Certificate ({counts.committed || 0})
            </Button>
          )}
          <Button
            onClick={() => openApproveAll(null)}
            disabled={!counts.pending}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="bp-text-fix-approve-all"
          >
            <ShieldCheck size={14} className="mr-1" />
            Approve All Pending ({counts.pending || 0})
          </Button>
          {(counts.error || 0) > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={dismissAllErrors}
              disabled={dismissingKey === '__bulk__'}
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="bp-text-fix-dismiss-all-errors"
            >
              {dismissingKey === '__bulk__'
                ? (<><Loader2 size={14} className="mr-1 animate-spin" /> Dismissing…</>)
                : (<><Ban size={14} className="mr-1" /> Dismiss All Errors ({counts.error || 0})</>)
              }
            </Button>
          )}
        </div>
      </div>

      {/* counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        {[
          { k: 'Total',     v: counts.total || 0,     c: 'text-gray-900' },
          { k: 'Pending',   v: counts.pending || 0,   c: 'text-amber-700' },
          { k: 'Committed', v: counts.committed || 0, c: 'text-emerald-700' },
          { k: 'Rejected',  v: counts.rejected || 0,  c: 'text-gray-700' },
          { k: 'Errors',    v: counts.error || 0,     c: 'text-rose-700' },
        ].map(s => (
          <div key={s.k} className="bg-gray-50 border rounded-md p-2 text-center">
            <div className="uppercase text-[10px] tracking-wide text-gray-500">{s.k}</div>
            <div className={`text-lg font-semibold ${s.c}`}>{s.v}</div>
          </div>
        ))}
      </div>

      {data.generated_at && (
        <div className="text-[11px] text-gray-500 flex items-center gap-1">
          <FileText size={11} /> Batch generated: {data.generated_at.slice(0, 19).replace('T', ' ')} UTC
          {data.run_id && <> · from audit run <code className="text-gray-600">{data.run_id}</code></>}
        </div>
      )}

      {/* scene grid — v6.35.34: hide-committed toggle to reduce visual clutter */}
      <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="bp-text-fix-hide-committed-label">
          <input
            type="checkbox"
            checked={hideCommitted}
            onChange={(e) => setHideCommitted(e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            data-testid="bp-text-fix-hide-committed-toggle"
          />
          <span>Hide committed scenes ({counts.committed || 0})</span>
        </label>
        <span className="text-gray-400">
          Showing {scenes.filter(s => !hideCommitted || s.status !== 'committed').length} of {scenes.length}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {scenes
          .filter(s => !hideCommitted || s.status !== 'committed')
          .map((s, i) => (
          <div
            key={`${s.grade}|${s.context}|${i}`}
            className={`border rounded-lg p-3 ${s.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 bg-white'}`}
            data-testid={`bp-text-fix-row-${i}`}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className="bg-white">Gr {s.grade}</Badge>
              <span className="text-sm font-semibold truncate flex-1 min-w-0">{s.context}</span>
              <Badge className={`border ${statusStyle(s.status)}`} variant="outline">{s.status}</Badge>
              <Badge variant="outline" className="bg-white text-gray-600">{s.question_count || 0} Qs</Badge>
            </div>

            {s.error ? (
              <div className="space-y-2">
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                  Generation error: {s.error}
                </div>
                <div className="flex items-center justify-end gap-2">
                  {/* v6.35.34: dismiss STALE/historical errors */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => dismissError(s)}
                    disabled={dismissingKey === `${s.grade}|${s.context}`}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                    data-testid={`bp-text-fix-dismiss-error-${i}`}
                    title="Clear the stored error string. Does NOT touch the live student image."
                  >
                    {dismissingKey === `${s.grade}|${s.context}`
                      ? <><Loader2 size={12} className="mr-1 animate-spin" /> Dismissing…</>
                      : <><Ban size={12} className="mr-1" /> Dismiss</>}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => retryScene(s)}
                    disabled={retryingKey === `${s.grade}|${s.context}`}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid={`bp-text-fix-retry-${i}`}
                  >
                    {retryingKey === `${s.grade}|${s.context}`
                      ? <><Loader2 size={12} className="mr-1 animate-spin" /> Retrying…</>
                      : <><Wand2 size={12} className="mr-1" /> Retry generation</>}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase text-gray-500 mb-1">Current (text violation)</div>
                  {s.current_url
                    ? <img src={s.current_url} alt="current" className="w-full h-32 object-cover border rounded" />
                    : <div className="w-full h-32 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-400">no image</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase text-emerald-700 mb-1">Candidate (text-free)</div>
                  {s.candidate_url
                    ? <img src={s.candidate_url} alt="candidate" className="w-full h-32 object-cover border border-emerald-300 rounded" />
                    : <div className="w-full h-32 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-400">n/a</div>}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-2">
              {s.status === 'pending' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => rejectScene(s)} data-testid={`bp-text-fix-reject-${i}`}>
                    <Ban size={12} className="mr-1" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => openApproveAll([{ grade: s.grade, context: s.context }])}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`bp-text-fix-approve-${i}`}
                  >
                    <CheckCircle2 size={12} className="mr-1" /> Approve
                  </Button>
                </>
              )}
              {s.status === 'rejected' && (
                <Button size="sm" variant="outline" onClick={() => unrejectScene(s)} data-testid={`bp-text-fix-restore-${i}`}>
                  <RefreshCw size={12} className="mr-1" /> Restore
                </Button>
              )}
              {s.status === 'committed' && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border text-[11px]" variant="outline">
                  <CheckCircle2 size={11} className="mr-1" /> Live in bp_images/
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* password dialog */}
      <Dialog open={pwOpen} onOpenChange={(v) => !v && setPwOpen(false)}>
        <DialogContent data-testid="bp-text-fix-approve-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Bulk Commit</DialogTitle>
            <DialogDescription>
              {selectedOnly
                ? `Commit ${selectedOnly.length} approved candidate${selectedOnly.length === 1 ? '' : 's'} to production.`
                : `Commit all ${counts.pending || 0} pending candidate${counts.pending === 1 ? '' : 's'} to production. Rejected scenes will be skipped.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 space-y-1">
              <div><strong>Production live.</strong> Each approval copies the candidate to
              <code className="bg-white px-1 mx-1 rounded">/bp_images/</code>
              and updates every question bound to that scene. A rollback manifest is written per scene
              + a bundle ID for single-command bulk revert.</div>
              <div className="pt-1 border-t border-amber-200">
                <strong>Press-PDF auto-regen.</strong> After commit, all 112 print-ready BLM booklet PDFs will be regenerated in the background (~2-3 min) so print files pick up the new images automatically. No separate click needed.
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Super Admin password</label>
              <Input
                type="password"
                value={pw}
                onChange={e => setPw(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && !approving && confirmApprove()}
                data-testid="bp-text-fix-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwOpen(false)} disabled={approving}>
              <XCircle size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              onClick={confirmApprove}
              disabled={approving || !pw}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bp-text-fix-confirm-approve"
            >
              {approving
                ? <><Loader2 size={14} className="mr-1 animate-spin" /> Committing…</>
                : <><ShieldCheck size={14} className="mr-1" /> Commit</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BPTextFixBatchPanel;
